import { TablePaginationConfig } from "antd/es/table/interface";

type UniqueFilterOptions<T> = {
  data?: T[];
  getKey?: (e: T) => string;
  getLabel?: (e: T) => string;
};

type UniqueFilterResult = { text: string; value: string };

/**
 * Build a unique filter for a table
 * @param data - The data to build the filter from
 * @param getKey - The function to get the key from the data
 * @param getLabel - The function to get the label from the data
 * @returns The unique filter
 */
export const buildUniqueFilter = <T>({
  data,
  getKey,
  getLabel,
}: UniqueFilterOptions<T>) => {
  if (!data || data.length === 0 || !getKey || !getLabel) {
    return [] as UniqueFilterResult[];
  }

  const map = new Map<string, string>();
  for (const row of data) {
    const key = getKey(row);
    const label = getLabel(row);

    if (key && label && !map.has(key)) {
      map.set(key, label);
    }
  }
  return Array.from(map.entries()).map(([value, text]) => ({
    text,
    value,
  })) as UniqueFilterResult[];
};

type TablePaginationOptions = {
  pagination: TablePaginationConfig;
  label?: string;
  onShowSizeChange?: (page: number, pageSize: number) => void;
  onChange?: (page: number, pageSize: number) => void;
};

export const getTablePagination = ({
  pagination,
  label = "items",
  onShowSizeChange,
  onChange,
}: TablePaginationOptions) => ({
  current: pagination.current,
  pageSize: pagination.pageSize,
  total: pagination.total,
  pageSizeOptions: pagination.pageSizeOptions,
  showSizeChanger: pagination.showSizeChanger,
  showQuickJumper: pagination.showQuickJumper,
  showTotal: (total: number, range: [number, number]) =>
    `${range[0]}-${range[1]} of ${total} ${label}`,
  onShowSizeChange,
  onChange,
});
